import { useCallback, useRef } from 'react';
import L from 'leaflet';
import 'leaflet-draw';

export default function usePlacementEditing({
  mapRef, layerMapRef, items, updatePlacement, pixelsPerFoot = 0,
  activePage, setActivePage,
  editingPlacement, setEditingPlacement,
}) {
  const originalCoordsRef = useRef(null);
  const editMarkerRef = useRef(null);

  const latLngToPixel = (ll) => ({ x: ll.lng, y: ll.lat });

  const polylineLengthFeet = (pts, ppf) => {
    if (!ppf || pts.length < 2) return 0;
    let total = 0;
    for (let i = 1; i < pts.length; i++) {
      const dx = pts[i].x - pts[i - 1].x;
      const dy = pts[i].y - pts[i - 1].y;
      total += Math.sqrt(dx * dx + dy * dy);
    }
    return total / ppf;
  };

  const polygonAreaSF = (pts, ppf) => {
    if (!ppf || pts.length < 3) return 0;
    let area = 0;
    for (let i = 0; i < pts.length; i++) {
      const j = (i + 1) % pts.length;
      area += pts[i].x * pts[j].y;
      area -= pts[j].x * pts[i].y;
    }
    return Math.abs(area / 2) / (ppf * ppf);
  };

  const startEdit = useCallback((itemId, placementId) => {
    const item = items.find(i => i.itemId === itemId);
    const placement = item?.placements?.find(p => p.id === placementId);
    if (!item || !placement) return;

    // Navigate to placement's page if needed
    if (placement.page !== activePage) {
      setActivePage(placement.page);
      setTimeout(() => startEdit(itemId, placementId), 100);
      return;
    }

    const key = `${itemId}::${placementId}`;
    const layer = layerMapRef.current?.[key];
    if (!layer) return;

    // Save original coords for cancel
    if (item.geometryType === 'point') {
      originalCoordsRef.current = { ...placement.pixelPath[0] };
    } else {
      originalCoordsRef.current = placement.pixelPath.map(p => ({ ...p }));
    }

    if (item.geometryType === 'point') {
      // Recreate as draggable marker
      const map = mapRef.current;
      const pos = [placement.pixelPath[0].y, placement.pixelPath[0].x];
      const dragMarker = L.marker(pos, {
        draggable: true,
        icon: L.divIcon({
          className: '',
          html: `<div style="width:20px;height:20px;border-radius:50%;background:${item.color || '#666'};border:3px solid #1976d2;box-shadow:0 0 8px rgba(25,118,210,0.6)"></div>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        }),
      });
      // Remove old static marker, add draggable one
      if (map) {
        const fg = Object.values(map._layers || {}).find(l => l.hasLayer && l.hasLayer(layer));
        if (fg) fg.removeLayer(layer);
        else map.removeLayer(layer);
        dragMarker.addTo(map);
      }
      editMarkerRef.current = dragMarker;
      layerMapRef.current[key] = dragMarker;
    } else {
      // Enable vertex editing for polygon/polyline
      if (layer.editing) {
        layer.editing.enable();
      }
    }

    setEditingPlacement({ itemId, placementId });
  }, [items, activePage, setActivePage, mapRef, layerMapRef]);

  const confirmEdit = useCallback(() => {
    if (!editingPlacement) return;
    const { itemId, placementId } = editingPlacement;
    const item = items.find(i => i.itemId === itemId);
    if (!item) { cancelEdit(); return; }

    const key = `${itemId}::${placementId}`;

    if (item.geometryType === 'point' && editMarkerRef.current) {
      const ll = editMarkerRef.current.getLatLng();
      const newPt = latLngToPixel(ll);
      updatePlacement(itemId, placementId, {
        pixelPath: [newPt],
        measuredQty: 1,
        measuredUnit: 'ea',
      });
    } else {
      const layer = layerMapRef.current?.[key];
      if (layer && layer.editing) {
        layer.editing.disable();
      }
      if (layer && layer.getLatLngs) {
        let latlngs = layer.getLatLngs();
        // Polygon wraps in extra array: [[latlngs]]
        while (Array.isArray(latlngs) && latlngs.length > 0 && Array.isArray(latlngs[0]) && !('lat' in latlngs[0])) {
          latlngs = latlngs[0];
        }
        const newPath = latlngs.map(latLngToPixel);

        let measuredQty = 0;
        let measuredUnit = '';
        if (item.geometryType === 'polygon') {
          measuredQty = polygonAreaSF(newPath, pixelsPerFoot);
          measuredUnit = pixelsPerFoot ? 'sf' : 'px²';
        } else if (item.geometryType === 'polyline') {
          measuredQty = polylineLengthFeet(newPath, pixelsPerFoot);
          measuredUnit = pixelsPerFoot ? 'lf' : 'px';
        }

        updatePlacement(itemId, placementId, { pixelPath: newPath, measuredQty, measuredUnit });
      }
    }

    editMarkerRef.current = null;
    originalCoordsRef.current = null;
    setEditingPlacement(null);
  }, [editingPlacement, items, updatePlacement, pixelsPerFoot, layerMapRef]);

  const cancelEdit = useCallback(() => {
    if (!editingPlacement) return;
    const { itemId, placementId } = editingPlacement;
    const item = items.find(i => i.itemId === itemId);
    const key = `${itemId}::${placementId}`;

    if (item?.geometryType === 'point' && editMarkerRef.current) {
      const map = mapRef.current;
      if (map) map.removeLayer(editMarkerRef.current);
      delete layerMapRef.current[key]; // force re-render on next cycle
    } else {
      const layer = layerMapRef.current?.[key];
      if (layer && layer.editing) layer.editing.disable();
      if (layer && originalCoordsRef.current && layer.setLatLngs) {
        const latlngs = originalCoordsRef.current.map(pt => [pt.y, pt.x]);
        layer.setLatLngs(latlngs);
      }
    }

    editMarkerRef.current = null;
    originalCoordsRef.current = null;
    setEditingPlacement(null);
  }, [editingPlacement, items, mapRef, layerMapRef]);

  return { editingPlacement, startEdit, confirmEdit, cancelEdit };
}
